import React, { useEffect, useState, useRef } from "react";
import "./Navbar.css";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useCart } from "../../context/CartContext";

const shopCategories = [
	{ label: "Balloon Bouquets", slug: "balloon-bouquets" },
	{ label: "Gift Baskets", slug: "gift-baskets" },
	{ label: "Balloon Arches", slug: "balloon-arches" },
	{ label: "Bubble Balloons", slug: "bubble-balloons" },
];

export const Navbar = ({ onContactClick }) => {
	const [sticky, setSticky] = useState(false);
	const [open, setOpen] = useState(false);
	const [shopOpen, setShopOpen] = useState(false);
	const [mobileShopOpen, setMobileShopOpen] = useState(false);
	const shopRef = useRef(null);
	const shopTimeout = useRef(null);
	const navigate = useNavigate();
	const { itemCount, setIsCartOpen } = useCart();

	function menuOpen() {
		setOpen(!open);
	}

	useEffect(() => {
		const handleScroll = () => {
			setSticky(window.scrollY > 100);
		};
		window.addEventListener("scroll", handleScroll);
		return () => window.removeEventListener("scroll", handleScroll);
	}, []);

	useEffect(() => {
		function handleClickOutside(e) {
			if (shopRef.current && !shopRef.current.contains(e.target)) {
				setShopOpen(false);
			}
		}
		document.addEventListener("mousedown", handleClickOutside);
		return () => document.removeEventListener("mousedown", handleClickOutside);
	}, []);

	function scrollTop() {
		window.scrollTo({ top: 0, behavior: "smooth" });
	}

	function handleCategoryClick(slug) {
		setShopOpen(false);
		setOpen(false);
		setMobileShopOpen(false);
		navigate(`/collections/${slug}`);
	}

	function handleShopAllClick() {
		setShopOpen(false);
		setOpen(false);
		setMobileShopOpen(false);
		navigate("/collections");
	}

	function handleContactClick() {
		setOpen(false);
		if (window.location.pathname === '/') {
			if (onContactClick) onContactClick();
		} else {
			navigate('/?scrollTo=contact');
		}
	}

	function handleShopEnter() {
		clearTimeout(shopTimeout.current);
		setShopOpen(true);
	}

	function handleShopLeave() {
		shopTimeout.current = setTimeout(() => {
			setShopOpen(false);
		}, 200);
	}

	return (
		<div>
			<header className={`header ${sticky ? 'sticky' : ''}`}>
				<nav className="nav-container">
					<Link to="/">
						<div className="logo" onClick={scrollTop}>
							<img src="/4d745950-00e9-4912-bc42-6cd050839f42.jpg" alt="Aire Studio" className="logo-img" />
							<span className="logo-text">Aire Studio</span>
						</div>
					</Link>

					<ul className="desktop-menu">
						<li>
							<NavLink className={({ isActive }) => isActive ? "active" : ""} to="/" end>
								Home
							</NavLink>
						</li>
						<li
							className="shop-dropdown"
							ref={shopRef}
							onMouseEnter={handleShopEnter}
							onMouseLeave={handleShopLeave}
						>
							<button className="nav-button shop-trigger" onClick={handleShopAllClick}>
								Shop
								<svg className={`shop-chevron ${shopOpen ? 'open' : ''}`} width="10" height="10" viewBox="0 0 10 10" fill="none">
									<path d="M2 4l3 3 3-3" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
								</svg>
							</button>
							<div className={`shop-dropdown-menu ${shopOpen ? 'active' : ''}`}>
								<div className="shop-dropdown-inner">
									{shopCategories.map((cat) => (
										<button
											key={cat.slug}
											className="shop-dropdown-item"
											onClick={() => handleCategoryClick(cat.slug)}
										>
											{cat.label}
										</button>
									))}
									<div className="shop-dropdown-divider" />
									<button className="shop-dropdown-item shop-dropdown-all" onClick={handleShopAllClick}>
										View All Collections
									</button>
								</div>
							</div>
						</li>
						<li>
							<NavLink className={({ isActive }) => isActive ? "active" : ""} to="/about">
								About
							</NavLink>
						</li>
						<li>
							<button className="nav-button" onClick={handleContactClick}>
								Contact
							</button>
						</li>
					</ul>

					<div className="nav-right">
						<button className="cart-icon-btn" onClick={() => setIsCartOpen(true)}>
							<svg width="20" height="20" viewBox="0 0 20 20" fill="none">
								<path d="M5 7h14l-1.5 7H6.5L5 7z" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round"/>
								<path d="M5 7L4 3H2" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
								<circle cx="8" cy="18" r="1" fill="currentColor"/>
								<circle cx="16" cy="18" r="1" fill="currentColor"/>
							</svg>
							{itemCount > 0 && (
								<span className="cart-badge">{itemCount}</span>
							)}
						</button>

						<div onClick={menuOpen} className={`hamburger ${open ? "active" : ""}`}>
							<span className="bar"></span>
							<span className="bar"></span>
							<span className="bar"></span>
						</div>
					</div>
				</nav>
			</header>

			<div className={`mobile-menu ${open ? "active" : ""}`}>
				<ul className="mobile-menu-list">
					<li>
						<Link to="/" className="mobile-menu-link" onClick={() => { setOpen(false); scrollTop(); }}>
							Home
						</Link>
					</li>
					<li className="mobile-shop-section">
						<button
							className="mobile-menu-item mobile-shop-toggle"
							onClick={() => setMobileShopOpen(!mobileShopOpen)}
						>
							Shop
							<svg className={`mobile-shop-chevron ${mobileShopOpen ? 'open' : ''}`} width="14" height="14" viewBox="0 0 10 10" fill="none">
								<path d="M2 4l3 3 3-3" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
							</svg>
						</button>
						<div className={`mobile-shop-submenu ${mobileShopOpen ? 'active' : ''}`}>
							{shopCategories.map((cat) => (
								<button
									key={cat.slug}
									className="mobile-shop-subitem"
									onClick={() => handleCategoryClick(cat.slug)}
								>
									{cat.label}
								</button>
							))}
							<button className="mobile-shop-subitem mobile-shop-all" onClick={handleShopAllClick}>
								View All
							</button>
						</div>
					</li>
					<li>
						<Link to="/about" className="mobile-menu-link" onClick={() => setOpen(false)}>
							About
						</Link>
					</li>
					<li>
						<button className="mobile-menu-item" onClick={handleContactClick}>
							Contact
						</button>
					</li>
				</ul>
			</div>
		</div>
	);
};
